import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestFishingSpotsInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-fishing-spots-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-rice-lake": "Rice Lake",
    "section-2-capilano-river": "Capilano River",
    "section-3-buntzen-lake": "Buntzen Lake",
    "section-4-como-lake": "Como Lake",
    "section-5-ambleside": "Ambleside Park",
    "section-6-jericho-pier": "Jericho Pier",
    "section-7-cates-park": "Cates Park",
    "section-8-fraser-river": "Fraser River",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www2.gov.bc.ca/gov/content/sports-culture/recreation/fishing-hunting/fishing/fishing-regulations",
      label: "BC Freshwater Fishing Regulations",
    },
    {
      url: "https://www.pac.dfo-mpo.gc.ca/fm-gp/rec/index-eng.html",
      label: "DFO Pacific Region Recreational Fishing",
    },
    {
      url: "https://www.gofishbc.com",
      label: "GoFishBC - Freshwater Fisheries Society of BC",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // Rice Lake
    {
      id: "section-1-rice-lake",
      lat: 49.35058,
      lng: -123.01583,
      popupText: "Rice Lake (North Vancouver)",
    },
    // Capilano River
    {
      id: "section-2-capilano-river",
      lat: 49.35649,
      lng: -123.11017,
      popupText: "Capilano River (Hatchery area)",
    },
    // Buntzen Lake
    {
      id: "section-3-buntzen-lake",
      lat: 49.34005,
      lng: -122.86035,
      popupText: "Buntzen Lake (Anmore)",
    },
    // Como Lake
    {
      id: "section-4-como-lake",
      lat: 49.26040,
      lng: -122.85830,
      popupText: "Como Lake (Coquitlam)",
    },
    // Ambleside Park
    {
      id: "section-5-ambleside",
      lat: 49.32481,
      lng: -123.15068,
      popupText: "Ambleside Park (West Vancouver)",
    },
    // Jericho Pier
    {
      id: "section-6-jericho-pier",
      lat: 49.27200,
      lng: -123.19200,
      popupText: "Jericho Pier (Vancouver)",
    },
    // Cates Park
    {
      id: "section-7-cates-park",
      lat: 49.30230,
      lng: -122.95412,
      popupText: "Cates Park (North Vancouver)",
    },
    // Fraser River
    {
      id: "section-8-fraser-river",
      lat: 49.12565,
      lng: -123.19640,
      popupText: "Garry Point Park / Fraser River (Richmond)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Fishing Spots Around Vancouver</title>
        <meta
          name="description"
          content="A comprehensive guide to some of the best fishing locations around Vancouver, covering both freshwater and saltwater spots."
        />
        <meta
          name="keywords"
          content="Vancouver, fishing, best spots, salmon, trout, capilano, buntzen, fraser river"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-fishing-spots-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Fishing Spots Around Vancouver"
        />
        <meta
          property="og:description"
          content="A complete, practical guide to prime fishing spots in and around Vancouver, BC."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-fishing-spots-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-fishing-spots-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Fishing Spots Around Vancouver"
        />
        <meta
          name="twitter:description"
          content="Discover top freshwater and saltwater fishing locations near Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-fishing-spots-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A scenic view of Vancouver's waters"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver, British Columbia, is a paradise for anglers looking for
              both saltwater and freshwater fishing experiences. From salmon
              runs in local rivers to tranquil trout fishing in urban lakes,
              Vancouver’s fishing opportunities are as diverse as they are
              accessible. This guide highlights a variety of fantastic fishing
              spots around the city, covering everything from gear tips to local
              regulations—so you can spend less time searching and more time
              casting your line.
            </p>

            {/* SECTION 1: Rice Lake */}
            <h2 id="section-1-rice-lake">Rice Lake</h2>
            <p
              ref={(el) => setSpotRef("section-1-rice-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Rice Lake</strong>{" "}
              <Marker
                coordY={49.35058}
                coordX={-123.01583}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Nestled in the Lower Seymour Conservation Reserve (North
              Vancouver), Rice Lake is a peaceful spot known for its abundance
              of stocked rainbow trout. Shore fishing is your best bet here—no
              boats allowed—and there is an accessible fishing dock at the south
              end. Parking is free, and a short walk on a gravel trail leads to
              the lake. Dogs aren’t allowed to protect local wildlife, making it
              a serene and family-friendly destination.
            </p>

            {/* SECTION 2: Capilano River */}
            <h2 id="section-2-capilano-river">Capilano River</h2>
            <p
              ref={(el) => setSpotRef("section-2-capilano-river", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Capilano River</strong>{" "}
              <Marker
                coordY={49.35649}
                coordX={-123.11017}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Flowing down from Cleveland Dam, the Capilano River offers
              excellent salmon fishing (particularly coho and chinook during
              their runs). Park near the Capilano River Hatchery for easy trail
              access to prime fishing pools. Pay close attention to changes in
              water levels: dam releases can raise the flow quickly. A tidal
              waters licence is needed if you plan to fish the lower estuary
              near Ambleside.
            </p>

            {/* SECTION 3: Buntzen Lake */}
            <h2 id="section-3-buntzen-lake">Buntzen Lake</h2>
            <p
              ref={(el) => setSpotRef("section-3-buntzen-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Buntzen Lake</strong>{" "}
              <Marker
                coordY={49.34005}
                coordX={-122.86035}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located in Anmore (near Port Moody), this reservoir offers
              shore-fishing and non-motorized boating opportunities for
              kokanee, rainbow trout, and cutthroat trout. With scenic mountain
              views and a loop trail surrounding the lake, Buntzen Lake is ideal
              for a day trip. Parking is free, but in peak summer, a
              reservation system may be in place due to high demand.
            </p>

            {/* SECTION 4: Como Lake */}
            <h2 id="section-4-como-lake">Como Lake</h2>
            <p
              ref={(el) => setSpotRef("section-4-como-lake", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Como Lake</strong>{" "}
              <Marker
                coordY={49.26040}
                coordX={-122.85830}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A small urban lake in Coquitlam, Como Lake is frequently stocked
              with rainbow trout, making it a perfect spot for families and
              novice anglers. Two fishing docks provide easy access, and the
              surrounding park includes a playground, washrooms, and walking
              paths.
            </p>

            {/* SECTION 5: Ambleside Park */}
            <h2 id="section-5-ambleside">Ambleside Park (Capilano River Mouth)</h2>
            <p
              ref={(el) => setSpotRef("section-5-ambleside", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Ambleside Park</strong>{" "}
              <Marker
                coordY={49.32481}
                coordX={-123.15068}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Situated where the Capilano River meets Burrard Inlet, this
              shore-based saltwater location offers seasonal salmon fishing
              without a boat. Look for chinook and coho staging near the river
              mouth in late summer. Parking availability ranges from free to
              pay, so watch signs carefully. A tidal waters licence (and salmon
              stamp if retaining salmon) is required.
            </p>

            {/* SECTION 6: Jericho Pier */}
            <h2 id="section-6-jericho-pier">Jericho Pier</h2>
            <p
              ref={(el) => setSpotRef("section-6-jericho-pier", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Jericho Pier</strong>{" "}
              <Marker
                coordY={49.27200}
                coordX={-123.19200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located in Vancouver’s Jericho Beach Park, this pier extends
              into English Bay, offering year-round opportunities to catch
              flatfish like starry flounder, perch, and the occasional salmon.
              Crabbing is also popular here. Parking is pay-by-the-hour in
              summer but may be free during off-peak seasons.
            </p>

            {/* SECTION 7: Cates Park */}
            <h2 id="section-7-cates-park">Cates Park (Whey-Ah-Wichen)</h2>
            <p
              ref={(el) => setSpotRef("section-7-cates-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Cates Park</strong>{" "}
              <Marker
                coordY={49.30230}
                coordX={-122.95412}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – North Vancouver’s largest waterfront park along Burrard Inlet,
              Cates Park offers a mix of beach, pier, and small-boat fishing.
              Expect groundfish like flounder, sole, and perch, plus pink salmon
              runs on odd-numbered years. Facilities include washrooms,
              playgrounds, and free parking. It’s a great spot to launch a kayak
              or small boat for more offshore fishing.
            </p>

            {/* SECTION 8: Fraser River */}
            <h2 id="section-8-fraser-river">Fraser River (Richmond/Steveston)</h2>
            <p
              ref={(el) => setSpotRef("section-8-fraser-river", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: Fraser River</strong>{" "}
              <Marker
                coordY={49.12565}
                coordX={-123.19640}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – The tidal reaches of the Fraser around Richmond and Steveston
              are famous for salmon runs (chinook, coho, chum, pink in odd years,
              and occasional sockeye openings). Shore fishing is common at Garry
              Point Park, while sturgeon fishing typically requires a guided
              boat tour. A tidal waters licence (plus sturgeon conservation
              licence endorsement if targeting sturgeon) is mandatory.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Licences Required:</strong> A provincial
              freshwater fishing licence is needed for lakes and rivers,
              whereas a federal tidal waters licence is required for
              saltwater or tidal portions of rivers. Barbless hooks
              are mandatory for salmon, and a salmon stamp must be added
              if you intend to keep any salmon. For sturgeon, you must have
              a sturgeon conservation licence, and all sturgeon must be
              released unharmed.
            </p>
            <p>
              <strong>Catch Limits & Seasons:</strong> Daily and possession
              limits vary by species and location. Salmon regulations in
              tidal areas can change with short notice, so always check for
              in-season updates. Urban lakes often have a limit of two or four
              trout per day. Some waterways may close seasonally to protect
              spawning fish.
            </p>
            <p>
              <strong>Ethical Fishing & Safety:</strong> Practice catch and
              release responsibly, use proper gear for the species, and be
              mindful of wildlife (including seals or bears). Respect other
              anglers and maintain a safe distance. Pack out any waste,
              including lines and hooks, to protect local ecosystems.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a fishing licence to fish in Vancouver?</h3>
              <p>
                Yes. Anglers 16 and older must have a valid licence. For
                freshwater (lakes and rivers), you need a BC freshwater licence.
                For saltwater or tidal areas (including the mouth of the Fraser
                River), you need a federal tidal waters licence.
              </p>

              <h3>2. When is the best time of year to fish near Vancouver?</h3>
              <p>
                Salmon fishing peaks July through September (coho, chinook, and
                pink in odd years). Trout fishing is best in spring and fall
                when local lakes are stocked. Sturgeon fishing is often
                productive in spring and fall along the Fraser River. However,
                regulations and conditions can vary; check official notices for
                closures or special openings.
              </p>

              <h3>3. Can I fish from shore without a boat?</h3>
              <p>
                Absolutely. All the spots mentioned here are shore-accessible
                (or have a public pier). Vancouver is fortunate to have easy
                shoreline access for both freshwater and saltwater fishing.
                Whether you choose Rice Lake for stocked trout or Ambleside for
                salmon, there are plenty of options requiring no boat.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s blend of urban life and natural beauty makes it an
              angler’s dream, offering a wide array of fishing options year
              round. Whether you’re casting for trout in a quiet lake or
              chasing salmon runs in the tidal Fraser, there’s always
              something to catch—and a scenic view to enjoy. By following
              local regulations, respecting the environment, and preparing
              with the right gear, you’ll ensure a rewarding fishing
              experience that preserves these waters for generations to
              come. Tight lines!
            </p>

            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}